import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import api from "../services/api";

export default function CompanyFinance() {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState("overview");
  const [loading, setLoading] = useState(true);
  
  // Data states
  const [summary, setSummary] = useState(null);
  const [incomes, setIncomes] = useState([]);
  const [expenses, setExpenses] = useState([]);
  const [categories, setCategories] = useState({ income: [], expense: [] });
  const [pendingExpenses, setPendingExpenses] = useState([]);
  
  // Form states
  const [incomeForm, setIncomeForm] = useState({
    category: "", amount: "", description: "", reference: "", source: ""
  });
  const [expenseForm, setExpenseForm] = useState({
    category: "", amount: "", description: "", reference: "", vendor: "", paymentMethod: "Cash", requiresApproval: false
  });
  
  // Category form states
  const [newCategoryForm, setNewCategoryForm] = useState({ name: "", type: "Income", description: "" });
  const [showCategoryModal, setShowCategoryModal] = useState(false);
  
  // Filter states
  const [dateFilter, setDateFilter] = useState({
    startDate: new Date(new Date().getFullYear(), 0, 1).toISOString().split('T')[0],
    endDate: new Date(new Date().getFullYear(), 11, 31).toISOString().split('T')[0]
  });

  useEffect(() => {
    checkAdminAccess();
  }, []);

  const checkAdminAccess = async () => {
    try {
      const res = await api.get("/Auth/me");
      if (!res.data.isAdmin) {
        navigate("/admin");
        return;
      }
      loadAllData();
    } catch (err) {
      navigate("/login");
    }
  };

  const loadAllData = async () => {
    setLoading(true);
    try {
      await Promise.all([
        loadSummary(),
        loadIncomes(),
        loadExpenses(),
        loadCategories(),
        loadPendingExpenses()
      ]);
    } catch (err) {
      console.error("Error loading data:", err);
    } finally {
      setLoading(false);
    }
  };

  const loadSummary = async () => {
    try {
      const res = await api.get(`/CompanyFinance/summary?startDate=${dateFilter.startDate}&endDate=${dateFilter.endDate}`);
      setSummary(res.data);
    } catch (err) {
      console.error("Error loading summary:", err);
    }
  };

  const loadIncomes = async () => {
    try {
      const res = await api.get(`/CompanyFinance/income?startDate=${dateFilter.startDate}&endDate=${dateFilter.endDate}`);
      setIncomes(res.data);
    } catch (err) {
      console.error("Error loading incomes:", err);
    }
  };

  const loadExpenses = async () => {
    try {
      const res = await api.get(`/CompanyFinance/expense?startDate=${dateFilter.startDate}&endDate=${dateFilter.endDate}`);
      setExpenses(res.data);
    } catch (err) {
      console.error("Error loading expenses:", err);
    }
  };

  const loadCategories = async () => {
    try {
      const [incomeRes, expenseRes] = await Promise.all([
        api.get("/CompanyFinance/categories/income"),
        api.get("/CompanyFinance/categories/expense")
      ]);
      setCategories({
        income: incomeRes.data,
        expense: expenseRes.data
      });
    } catch (err) {
      await api.post("/CompanyFinance/categories/init");
      loadCategories();
    }
  };

  const loadPendingExpenses = async () => {
    try {
      const res = await api.get("/CompanyFinance/expense?status=Pending&pageSize=100");
      setPendingExpenses(res.data);
    } catch (err) {
      console.error("Error loading pending expenses:", err);
    }
  };

  const handlePostIncome = async (e) => {
    e.preventDefault();
    if (!incomeForm.category || !incomeForm.amount || !incomeForm.description) {
      alert("Please fill in all required fields");
      return;
    }
    
    try {
      await api.post("/CompanyFinance/income", {
        ...incomeForm,
        amount: parseFloat(incomeForm.amount)
      });
      alert(`✅ Income posted: ₦${parseFloat(incomeForm.amount).toLocaleString()}`);
      setIncomeForm({ category: "", amount: "", description: "", reference: "", source: "" });
      loadAllData();
    } catch (err) {
      alert(err.response?.data?.message || "Failed to post income");
    }
  };

  const handlePostExpense = async (e) => {
    e.preventDefault();
    if (!expenseForm.category || !expenseForm.amount || !expenseForm.description) {
      alert("Please fill in all required fields");
      return;
    }
    
    try {
      await api.post("/CompanyFinance/expense", {
        ...expenseForm,
        amount: parseFloat(expenseForm.amount)
      });
      alert(`✅ Expense posted: ₦${parseFloat(expenseForm.amount).toLocaleString()}`);
      setExpenseForm({ category: "", amount: "", description: "", reference: "", vendor: "", paymentMethod: "Cash", requiresApproval: false });
      loadAllData();
    } catch (err) {
      alert(err.response?.data?.message || "Failed to post expense");
    }
  };

  const handleApproveExpense = async (id) => {
    if (!window.confirm("Are you sure you want to approve this expense?")) return;
    
    try {
      await api.post(`/CompanyFinance/expense/${id}/approve`);
      alert("✅ Expense approved successfully!");
      loadAllData();
    } catch (err) {
      console.error("Approve error:", err);
      const message = err.response?.data?.message || "";
      
      if (message.includes("not pending")) {
        alert("ℹ️ This expense has already been processed.");
      } else {
        alert("❌ Failed to approve expense: " + message);
      }
      loadAllData();
    }
  };

  const handleDeleteIncome = async (id) => {
    if (!window.confirm("Are you sure you want to delete this income entry?")) return;
    
    try {
      await api.delete(`/CompanyFinance/income/${id}`);
      alert("Income deleted");
      loadIncomes();
      loadSummary();
    } catch (err) {
      alert(err.response?.data?.message || "Failed to delete income");
    }
  };

  const handleDeleteExpense = async (id) => {
    if (!window.confirm("Are you sure you want to delete this expense entry?")) return;
    
    try {
      await api.delete(`/CompanyFinance/expense/${id}`);
      alert("Expense deleted");
      loadExpenses();
      loadSummary();
    } catch (err) {
      alert(err.response?.data?.message || "Failed to delete expense");
    }
  };

  // Category Management Functions
  const handleCreateCategory = async (e) => {
    e.preventDefault();
    if (!newCategoryForm.name || !newCategoryForm.type) {
      alert("Please fill in category name and type");
      return;
    }
    
    try {
      await api.post("/CompanyFinance/categories", {
        name: newCategoryForm.name,
        type: newCategoryForm.type,
        description: newCategoryForm.description
      });
      alert(`✅ Category "${newCategoryForm.name}" created successfully!`);
      setNewCategoryForm({ name: "", type: "Income", description: "" });
      setShowCategoryModal(false);
      loadCategories();
    } catch (err) {
      alert(err.response?.data?.message || "Failed to create category");
    }
  };

  const handleDeleteCategory = async (id, name) => {
    if (!window.confirm(`Are you sure you want to delete the category "${name}"?\n\nNote: This will not delete transactions using this category.`)) return;
    
    try {
      await api.delete(`/CompanyFinance/categories/${id}`);
      alert(`Category "${name}" deleted`);
      loadCategories();
    } catch (err) {
      alert(err.response?.data?.message || "Failed to delete category");
    }
  };

  const formatCurrency = (amount) => `₦${(amount || 0).toLocaleString("en-NG", { minimumFractionDigits: 2 })}`;
  const formatDate = (date) => new Date(date).toLocaleDateString("en-NG", { day: "2-digit", month: "short", year: "numeric" });

  const styles = {
    container: { padding: "20px", maxWidth: "1400px", margin: "0 auto", fontFamily: "Arial, sans-serif" },
    header: { display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "20px" },
    title: { fontSize: "24px", fontWeight: "bold", color: "#1a1a2e" },
    backBtn: { padding: "10px 20px", background: "#6c757d", color: "white", border: "none", borderRadius: "8px", cursor: "pointer" },
    tabs: { display: "flex", gap: "10px", marginBottom: "20px", flexWrap: "wrap" },
    tab: { padding: "12px 24px", border: "none", borderRadius: "8px", cursor: "pointer", fontWeight: "500", transition: "all 0.3s" },
    activeTab: { background: "#16a34a", color: "white" },
    inactiveTab: { background: "#e5e7eb", color: "#374151" },
    card: { background: "white", borderRadius: "12px", padding: "20px", marginBottom: "20px", boxShadow: "0 2px 8px rgba(0,0,0,0.1)" },
    statsGrid: { display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))", gap: "15px", marginBottom: "20px" },
    statCard: { background: "white", borderRadius: "12px", padding: "20px", boxShadow: "0 2px 8px rgba(0,0,0,0.1)" },
    statValue: { fontSize: "24px", fontWeight: "bold" },
    statLabel: { fontSize: "14px", color: "#6b7280" },
    form: { display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))", gap: "15px" },
    formGroup: { display: "flex", flexDirection: "column", gap: "5px" },
    label: { fontWeight: "600", color: "#374151", fontSize: "14px" },
    input: { padding: "12px", border: "1px solid #d1d5db", borderRadius: "8px", fontSize: "14px" },
    select: { padding: "12px", border: "1px solid #d1d5db", borderRadius: "8px", fontSize: "14px" },
    textarea: { padding: "12px", border: "1px solid #d1d5db", borderRadius: "8px", fontSize: "14px", minHeight: "80px", resize: "vertical" },
    submitBtn: { padding: "14px 24px", border: "none", borderRadius: "8px", cursor: "pointer", fontWeight: "600", fontSize: "16px" },
    incomeBtn: { background: "#16a34a", color: "white" },
    expenseBtn: { background: "#dc2626", color: "white" },
    table: { width: "100%", borderCollapse: "collapse" },
    th: { padding: "12px", textAlign: "left", borderBottom: "2px solid #e5e7eb", fontWeight: "600", color: "#374151" },
    td: { padding: "12px", borderBottom: "1px solid #e5e7eb" },
    badge: { padding: "4px 12px", borderRadius: "20px", fontSize: "12px", fontWeight: "500" },
    completedBadge: { background: "#dcfce7", color: "#16a34a" },
    pendingBadge: { background: "#fef3c7", color: "#d97706" },
    deleteBtn: { padding: "6px 12px", background: "#fee2e2", color: "#dc2626", border: "none", borderRadius: "6px", cursor: "pointer", fontSize: "12px" },
    approveBtn: { padding: "6px 12px", background: "#dcfce7", color: "#16a34a", border: "none", borderRadius: "6px", cursor: "pointer", fontSize: "12px", marginRight: "5px" },
    filterRow: { display: "flex", gap: "15px", alignItems: "center", marginBottom: "20px", flexWrap: "wrap" },
    checkbox: { width: "18px", height: "18px", marginRight: "8px" },
    // Modal styles
    modal: { position: "fixed", top: 0, left: 0, right: 0, bottom: 0, background: "rgba(0,0,0,0.5)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1000 },
    modalContent: { background: "white", borderRadius: "12px", padding: "30px", width: "90%", maxWidth: "500px", boxShadow: "0 4px 20px rgba(0,0,0,0.2)" },
    modalHeader: { display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "20px" },
    modalTitle: { fontSize: "20px", fontWeight: "bold", color: "#1a1a2e" },
    closeBtn: { background: "none", border: "none", fontSize: "24px", cursor: "pointer", color: "#6b7280" },
    // Category styles
    categoryGrid: { display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(300px, 1fr))", gap: "20px" },
    categoryCard: { background: "white", borderRadius: "12px", padding: "20px", boxShadow: "0 2px 8px rgba(0,0,0,0.1)" },
    categoryHeader: { display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "15px" },
    categoryList: { display: "flex", flexDirection: "column", gap: "8px" },
    categoryItem: { display: "flex", justifyContent: "space-between", alignItems: "center", padding: "10px 15px", background: "#f9fafb", borderRadius: "8px", border: "1px solid #e5e7eb" },
    categoryName: { fontWeight: "500", color: "#374151" },
    addCategoryBtn: { padding: "10px 20px", background: "#3b82f6", color: "white", border: "none", borderRadius: "8px", cursor: "pointer", fontWeight: "600", fontSize: "14px" },
    incomeCategoryBadge: { background: "#dcfce7", color: "#16a34a", padding: "2px 8px", borderRadius: "4px", fontSize: "11px", fontWeight: "600" },
    expenseCategoryBadge: { background: "#fee2e2", color: "#dc2626", padding: "2px 8px", borderRadius: "4px", fontSize: "11px", fontWeight: "600" }
  };

  if (loading) {
    return (
      <div style={{ ...styles.container, textAlign: "center", paddingTop: "100px" }}>
        <div style={{ fontSize: "24px" }}>Loading...</div>
      </div>
    );
  }

  return (
    <div style={styles.container}>
      {/* Header */}
      <div style={styles.header}>
        <h1 style={styles.title}>💰 Company Finance</h1>
        <button style={styles.backBtn} onClick={() => navigate("/admin")}>← Back to Admin</button>
      </div>

      {/* Tabs */}
      <div style={styles.tabs}>
        {[
          { id: "overview", label: "📊 Overview" },
          { id: "income", label: "💵 Post Income" },
          { id: "expense", label: "💸 Post Expense" },
          { id: "history", label: "📋 Transaction History" },
          { id: "pending", label: `⏳ Pending (${pendingExpenses.length})` },
          { id: "categories", label: "🏷️ Categories" }
        ].map(tab => (
          <button
            key={tab.id}
            style={{ ...styles.tab, ...(activeTab === tab.id ? styles.activeTab : styles.inactiveTab) }}
            onClick={() => setActiveTab(tab.id)}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {/* Date Filter - Show on most tabs except categories */}
      {activeTab !== "categories" && (
        <div style={styles.filterRow}>
          <div style={styles.formGroup}>
            <label style={styles.label}>From Date</label>
            <input
              type="date"
              style={styles.input}
              value={dateFilter.startDate}
              onChange={(e) => setDateFilter({ ...dateFilter, startDate: e.target.value })}
            />
          </div>
          <div style={styles.formGroup}>
            <label style={styles.label}>To Date</label>
            <input
              type="date"
              style={styles.input}
              value={dateFilter.endDate}
              onChange={(e) => setDateFilter({ ...dateFilter, endDate: e.target.value })}
            />
          </div>
          <button
            style={{ ...styles.submitBtn, background: "#3b82f6", color: "white", alignSelf: "flex-end" }}
            onClick={loadAllData}
          >
            🔍 Filter
          </button>
        </div>
      )}

      {/* Overview Tab */}
      {activeTab === "overview" && (
        <div>
          <div style={styles.statsGrid}>
            <div style={{ ...styles.statCard, borderLeft: "4px solid #16a34a" }}>
              <div style={{ ...styles.statValue, color: "#16a34a" }}>{formatCurrency(summary?.totalIncome)}</div>
              <div style={styles.statLabel}>Total Income</div>
            </div>
            <div style={{ ...styles.statCard, borderLeft: "4px solid #dc2626" }}>
              <div style={{ ...styles.statValue, color: "#dc2626" }}>{formatCurrency(summary?.totalExpenses)}</div>
              <div style={styles.statLabel}>Total Expenses</div>
            </div>
            <div style={{ ...styles.statCard, borderLeft: "4px solid #3b82f6" }}>
              <div style={{ ...styles.statValue, color: summary?.netProfit >= 0 ? "#16a34a" : "#dc2626" }}>
                {formatCurrency(summary?.netProfit)}
              </div>
              <div style={styles.statLabel}>Net Profit/Loss</div>
            </div>
            <div style={{ ...styles.statCard, borderLeft: "4px solid #8b5cf6" }}>
              <div style={{ ...styles.statValue, color: "#8b5cf6" }}>{summary?.profitMargin || 0}%</div>
              <div style={styles.statLabel}>Profit Margin</div>
            </div>
          </div>

          {/* Income by Category */}
          <div style={styles.card}>
            <h3 style={{ marginBottom: "15px" }}>📈 Income by Category</h3>
            {summary?.incomeByCategory?.length > 0 ? (
              <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(150px, 1fr))", gap: "10px" }}>
                {summary.incomeByCategory.map((item, idx) => (
                  <div key={idx} style={{ padding: "10px", background: "#f0fdf4", borderRadius: "8px" }}>
                    <div style={{ fontWeight: "600", color: "#16a34a" }}>{formatCurrency(item.amount)}</div>
                    <div style={{ fontSize: "12px", color: "#6b7280" }}>{item.category}</div>
                  </div>
                ))}
              </div>
            ) : (
              <p style={{ color: "#6b7280" }}>No income recorded in this period</p>
            )}
          </div>

          {/* Expenses by Category */}
          <div style={styles.card}>
            <h3 style={{ marginBottom: "15px" }}>📉 Expenses by Category</h3>
            {summary?.expensesByCategory?.length > 0 ? (
              <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(150px, 1fr))", gap: "10px" }}>
                {summary.expensesByCategory.map((item, idx) => (
                  <div key={idx} style={{ padding: "10px", background: "#fef2f2", borderRadius: "8px" }}>
                    <div style={{ fontWeight: "600", color: "#dc2626" }}>{formatCurrency(item.amount)}</div>
                    <div style={{ fontSize: "12px", color: "#6b7280" }}>{item.category}</div>
                  </div>
                ))}
              </div>
            ) : (
              <p style={{ color: "#6b7280" }}>No expenses recorded in this period</p>
            )}
          </div>
        </div>
      )}

      {/* Post Income Tab */}
      {activeTab === "income" && (
        <div style={styles.card}>
          <h3 style={{ marginBottom: "20px", color: "#16a34a" }}>💵 Post New Income</h3>
          <form onSubmit={handlePostIncome}>
            <div style={styles.form}>
              <div style={styles.formGroup}>
                <label style={styles.label}>Category *</label>
                <select
                  style={styles.select}
                  value={incomeForm.category}
                  onChange={(e) => setIncomeForm({ ...incomeForm, category: e.target.value })}
                  required
                >
                  <option value="">Select Category</option>
                  {categories.income.map(cat => (
                    <option key={cat.id} value={cat.name}>{cat.name}</option>
                  ))}
                </select>
              </div>
              <div style={styles.formGroup}>
                <label style={styles.label}>Amount (₦) *</label>
                <input
                  type="number"
                  style={styles.input}
                  placeholder="0.00"
                  value={incomeForm.amount}
                  onChange={(e) => setIncomeForm({ ...incomeForm, amount: e.target.value })}
                  required
                  min="0.01"
                  step="0.01"
                />
              </div>
              <div style={styles.formGroup}>
                <label style={styles.label}>Reference</label>
                <input
                  type="text"
                  style={styles.input}
                  placeholder="Invoice #, Receipt #, etc."
                  value={incomeForm.reference}
                  onChange={(e) => setIncomeForm({ ...incomeForm, reference: e.target.value })}
                />
              </div>
              <div style={styles.formGroup}>
                <label style={styles.label}>Source</label>
                <input
                  type="text"
                  style={styles.input}
                  placeholder="Where the income came from"
                  value={incomeForm.source}
                  onChange={(e) => setIncomeForm({ ...incomeForm, source: e.target.value })}
                />
              </div>
              <div style={{ ...styles.formGroup, gridColumn: "1 / -1" }}>
                <label style={styles.label}>Description *</label>
                <textarea
                  style={styles.textarea}
                  placeholder="Describe this income..."
                  value={incomeForm.description}
                  onChange={(e) => setIncomeForm({ ...incomeForm, description: e.target.value })}
                  required
                />
              </div>
            </div>
            <button type="submit" style={{ ...styles.submitBtn, ...styles.incomeBtn, marginTop: "20px" }}>
              ✅ Post Income
            </button>
          </form>
        </div>
      )}

      {/* Post Expense Tab */}
      {activeTab === "expense" && (
        <div style={styles.card}>
          <h3 style={{ marginBottom: "20px", color: "#dc2626" }}>💸 Post New Expense</h3>
          <form onSubmit={handlePostExpense}>
            <div style={styles.form}>
              <div style={styles.formGroup}>
                <label style={styles.label}>Category *</label>
                <select
                  style={styles.select}
                  value={expenseForm.category}
                  onChange={(e) => setExpenseForm({ ...expenseForm, category: e.target.value })}
                  required
                >
                  <option value="">Select Category</option>
                  {categories.expense.map(cat => (
                    <option key={cat.id} value={cat.name}>{cat.name}</option>
                  ))}
                </select>
              </div>
              <div style={styles.formGroup}>
                <label style={styles.label}>Amount (₦) *</label>
                <input
                  type="number"
                  style={styles.input}
                  placeholder="0.00"
                  value={expenseForm.amount}
                  onChange={(e) => setExpenseForm({ ...expenseForm, amount: e.target.value })}
                  required
                  min="0.01"
                  step="0.01"
                />
              </div>
              <div style={styles.formGroup}>
                <label style={styles.label}>Vendor/Payee</label>
                <input
                  type="text"
                  style={styles.input}
                  placeholder="Who was paid"
                  value={expenseForm.vendor}
                  onChange={(e) => setExpenseForm({ ...expenseForm, vendor: e.target.value })}
                />
              </div>
              <div style={styles.formGroup}>
                <label style={styles.label}>Payment Method</label>
                <select
                  style={styles.select}
                  value={expenseForm.paymentMethod}
                  onChange={(e) => setExpenseForm({ ...expenseForm, paymentMethod: e.target.value })}
                >
                  <option value="Cash">Cash</option>
                  <option value="Bank Transfer">Bank Transfer</option>
                  <option value="Card">Card</option>
                  <option value="Cheque">Cheque</option>
                  <option value="Mobile Money">Mobile Money</option>
                </select>
              </div>
              <div style={styles.formGroup}>
                <label style={styles.label}>Reference</label>
                <input
                  type="text"
                  style={styles.input}
                  placeholder="Invoice #, Receipt #, etc."
                  value={expenseForm.reference}
                  onChange={(e) => setExpenseForm({ ...expenseForm, reference: e.target.value })}
                />
              </div>
              <div style={{ ...styles.formGroup, display: "flex", alignItems: "center", paddingTop: "25px" }}>
                <input
                  type="checkbox"
                  style={styles.checkbox}
                  checked={expenseForm.requiresApproval}
                  onChange={(e) => setExpenseForm({ ...expenseForm, requiresApproval: e.target.checked })}
                />
                <label style={{ ...styles.label, margin: 0 }}>Requires Approval</label>
              </div>
              <div style={{ ...styles.formGroup, gridColumn: "1 / -1" }}>
                <label style={styles.label}>Description *</label>
                <textarea
                  style={styles.textarea}
                  placeholder="Describe this expense..."
                  value={expenseForm.description}
                  onChange={(e) => setExpenseForm({ ...expenseForm, description: e.target.value })}
                  required
                />
              </div>
            </div>
            <button type="submit" style={{ ...styles.submitBtn, ...styles.expenseBtn, marginTop: "20px" }}>
              💸 Post Expense
            </button>
          </form>
        </div>
      )}

      {/* Transaction History Tab */}
      {activeTab === "history" && (
        <div>
          {/* Income History */}
          <div style={styles.card}>
            <h3 style={{ marginBottom: "15px", color: "#16a34a" }}>📈 Income History ({incomes.length})</h3>
            {incomes.length > 0 ? (
              <div style={{ overflowX: "auto" }}>
                <table style={styles.table}>
                  <thead>
                    <tr>
                      <th style={styles.th}>Date</th>
                      <th style={styles.th}>Category</th>
                      <th style={styles.th}>Amount</th>
                      <th style={styles.th}>Description</th>
                      <th style={styles.th}>Posted By</th>
                      <th style={styles.th}>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {incomes.map(income => (
                      <tr key={income.id}>
                        <td style={styles.td}>{formatDate(income.transactionDate)}</td>
                        <td style={styles.td}>{income.category}</td>
                        <td style={{ ...styles.td, color: "#16a34a", fontWeight: "600" }}>{formatCurrency(income.amount)}</td>
                        <td style={styles.td}>{income.description}</td>
                        <td style={styles.td}>{income.postedByName}</td>
                        <td style={styles.td}>
                          <button style={styles.deleteBtn} onClick={() => handleDeleteIncome(income.id)}>Delete</button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <p style={{ color: "#6b7280" }}>No income records found</p>
            )}
          </div>

          {/* Expense History */}
          <div style={styles.card}>
            <h3 style={{ marginBottom: "15px", color: "#dc2626" }}>📉 Expense History ({expenses.length})</h3>
            {expenses.length > 0 ? (
              <div style={{ overflowX: "auto" }}>
                <table style={styles.table}>
                  <thead>
                    <tr>
                      <th style={styles.th}>Date</th>
                      <th style={styles.th}>Category</th>
                      <th style={styles.th}>Amount</th>
                      <th style={styles.th}>Vendor</th>
                      <th style={styles.th}>Description</th>
                      <th style={styles.th}>Status</th>
                      <th style={styles.th}>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {expenses.map(expense => (
                      <tr key={expense.id}>
                        <td style={styles.td}>{formatDate(expense.transactionDate)}</td>
                        <td style={styles.td}>{expense.category}</td>
                        <td style={{ ...styles.td, color: "#dc2626", fontWeight: "600" }}>{formatCurrency(expense.amount)}</td>
                        <td style={styles.td}>{expense.vendor || "-"}</td>
                        <td style={styles.td}>{expense.description}</td>
                        <td style={styles.td}>
                          <span style={{ ...styles.badge, ...(expense.status === "Completed" ? styles.completedBadge : styles.pendingBadge) }}>
                            {expense.status}
                          </span>
                        </td>
                        <td style={styles.td}>
                          <button style={styles.deleteBtn} onClick={() => handleDeleteExpense(expense.id)}>Delete</button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <p style={{ color: "#6b7280" }}>No expense records found</p>
            )}
          </div>
        </div>
      )}

      {/* Pending Approvals Tab */}
      {activeTab === "pending" && (
        <div style={styles.card}>
          <h3 style={{ marginBottom: "15px", color: "#d97706" }}>⏳ Pending Expense Approvals</h3>
          {pendingExpenses.length > 0 ? (
            <div style={{ overflowX: "auto" }}>
              <table style={styles.table}>
                <thead>
                  <tr>
                    <th style={styles.th}>Date</th>
                    <th style={styles.th}>Category</th>
                    <th style={styles.th}>Amount</th>
                    <th style={styles.th}>Vendor</th>
                    <th style={styles.th}>Description</th>
                    <th style={styles.th}>Posted By</th>
                    <th style={styles.th}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {pendingExpenses.map(expense => (
                    <tr key={expense.id}>
                      <td style={styles.td}>{formatDate(expense.transactionDate)}</td>
                      <td style={styles.td}>{expense.category}</td>
                      <td style={{ ...styles.td, color: "#dc2626", fontWeight: "600" }}>{formatCurrency(expense.amount)}</td>
                      <td style={styles.td}>{expense.vendor || "-"}</td>
                      <td style={styles.td}>{expense.description}</td>
                      <td style={styles.td}>{expense.postedByName}</td>
                      <td style={styles.td}>
                        <button style={styles.approveBtn} onClick={() => handleApproveExpense(expense.id)}>✅ Approve</button>
                        <button style={styles.deleteBtn} onClick={() => handleDeleteExpense(expense.id)}>❌ Reject</button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <p style={{ color: "#6b7280", textAlign: "center", padding: "40px" }}>
              ✅ No pending expenses to approve
            </p>
          )}
        </div>
      )}

      {/* Categories Tab */}
      {activeTab === "categories" && (
        <div>
          {/* Add Category Button */}
          <div style={{ marginBottom: "20px" }}>
            <button 
              style={styles.addCategoryBtn}
              onClick={() => setShowCategoryModal(true)}
            >
              ➕ Add New Category
            </button>
          </div>

          {/* Categories Grid */}
          <div style={styles.categoryGrid}>
            {/* Income Categories */}
            <div style={styles.categoryCard}>
              <div style={styles.categoryHeader}>
                <h3 style={{ color: "#16a34a", margin: 0 }}>💵 Income Categories</h3>
                <span style={styles.incomeCategoryBadge}>{categories.income.length} categories</span>
              </div>
              <div style={styles.categoryList}>
                {categories.income.length > 0 ? (
                  categories.income.map(cat => (
                    <div key={cat.id} style={styles.categoryItem}>
                      <span style={styles.categoryName}>{cat.name}</span>
                      <button 
                        style={styles.deleteBtn}
                        onClick={() => handleDeleteCategory(cat.id, cat.name)}
                      >
                        🗑️ Delete
                      </button>
                    </div>
                  ))
                ) : (
                  <p style={{ color: "#6b7280", textAlign: "center", padding: "20px" }}>No income categories</p>
                )}
              </div>
            </div>

            {/* Expense Categories */}
            <div style={styles.categoryCard}>
              <div style={styles.categoryHeader}>
                <h3 style={{ color: "#dc2626", margin: 0 }}>💸 Expense Categories</h3>
                <span style={styles.expenseCategoryBadge}>{categories.expense.length} categories</span>
              </div>
              <div style={styles.categoryList}>
                {categories.expense.length > 0 ? (
                  categories.expense.map(cat => (
                    <div key={cat.id} style={styles.categoryItem}>
                      <span style={styles.categoryName}>{cat.name}</span>
                      <button 
                        style={styles.deleteBtn}
                        onClick={() => handleDeleteCategory(cat.id, cat.name)}
                      >
                        🗑️ Delete
                      </button>
                    </div>
                  ))
                ) : (
                  <p style={{ color: "#6b7280", textAlign: "center", padding: "20px" }}>No expense categories</p>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Add Category Modal */}
      {showCategoryModal && (
        <div style={styles.modal} onClick={() => setShowCategoryModal(false)}>
          <div style={styles.modalContent} onClick={(e) => e.stopPropagation()}>
            <div style={styles.modalHeader}>
              <h3 style={styles.modalTitle}>➕ Add New Category</h3>
              <button style={styles.closeBtn} onClick={() => setShowCategoryModal(false)}>×</button>
            </div>
            <form onSubmit={handleCreateCategory}>
              <div style={styles.formGroup}>
                <label style={styles.label}>Category Type *</label>
                <select
                  style={styles.select}
                  value={newCategoryForm.type}
                  onChange={(e) => setNewCategoryForm({ ...newCategoryForm, type: e.target.value })}
                  required
                >
                  <option value="Income">Income</option>
                  <option value="Expense">Expense</option>
                </select>
              </div>
              <div style={{ ...styles.formGroup, marginTop: "15px" }}>
                <label style={styles.label}>Category Name *</label>
                <input
                  type="text"
                  style={styles.input}
                  placeholder="e.g., Sales Revenue, Office Supplies"
                  value={newCategoryForm.name}
                  onChange={(e) => setNewCategoryForm({ ...newCategoryForm, name: e.target.value })}
                  required
                />
              </div>
              <div style={{ ...styles.formGroup, marginTop: "15px" }}>
                <label style={styles.label}>Description (Optional)</label>
                <textarea
                  style={{ ...styles.textarea, minHeight: "60px" }}
                  placeholder="Brief description of this category..."
                  value={newCategoryForm.description}
                  onChange={(e) => setNewCategoryForm({ ...newCategoryForm, description: e.target.value })}
                />
              </div>
              <div style={{ display: "flex", gap: "10px", marginTop: "20px" }}>
                <button 
                  type="submit" 
                  style={{ ...styles.submitBtn, background: "#3b82f6", color: "white", flex: 1 }}
                >
                  ✅ Create Category
                </button>
                <button 
                  type="button" 
                  style={{ ...styles.submitBtn, background: "#6b7280", color: "white" }}
                  onClick={() => setShowCategoryModal(false)}
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
